import React, { useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  TextField,
} from '@mui/material';
import {
  buildPublishBody,
  createPendingIdentity,
  discardPendingIdentity,
  generateRecoveryKey,
  holdSessionKeys,
  publishIdentity,
  publishLanded,
  wrapForRecoveryKey,
} from '../../../services/chat/chatKeys';
import type { PendingIdentity } from '../../../services/chat/chatKeys';
import {
  PASSKEY_UNSUPPORTED_MESSAGE,
  PasskeyError,
  createIdentityPasskey,
  defaultPasskeyName,
} from '../../../services/chat/chatPasskeys';
import type { CreatedPasskey } from '../../../services/chat/chatPasskeys';
import type { UserKeys } from '../../../services/chat/chatService';
import { trustBrowser } from '../../../services/chat/chatTrustedBrowser';
import type { WrappedPrivateKey } from '../../../services/chat/chatCrypto';
import { RecoveryKeyStep } from './RecoveryKeyStep';

export const CHALLENGE_EXPIRED_MESSAGE =
  'Your passkey challenge expired. Touch your passkey once more to finish setup.';

type SetupStep = 'preparing' | 'passkey' | 'recovery' | 'uploading' | 'unsupported' | 'failed';

export interface KeySetupDialogProps {
  open: boolean;
  uid: string;
  /** The account e-mail (uid when unavailable) — the recovery file and the passkey user name use it. */
  email: string;
  /** Called with the published key document once setup succeeds and this browser is trusted. */
  onComplete: (keys: UserKeys) => void;
  /** A full identity already exists (409 `identity_exists`): the generated material is discarded. */
  onIdentityExists: () => void;
  /** No passkey path returned a PRF output and the researcher closed the dialog: the generated material is discarded. */
  onUnsupported: () => void;
}

/**
 * One-time chat encryption setup, in the spec's order: keypair → prfSalt →
 * registration options → passkey (PRF at creation or the one-assertion
 * fallback) → wrap → recovery key shown, downloaded, and re-entered → wrap →
 * one upload → trusted browser. Not dismissable until setup completes, except
 * from the unsupported-browser state, which closes into an inline message.
 */
export function KeySetupDialog({ open, uid, email, onComplete, onIdentityExists, onUnsupported }: KeySetupDialogProps) {
  const [step, setStep] = useState<SetupStep>('preparing');
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [recoveryKey, setRecoveryKey] = useState<string | null>(null);
  const [prepareAttempt, setPrepareAttempt] = useState(0);
  const identityRef = useRef<PendingIdentity | null>(null);
  const passkeyRef = useRef<CreatedPasskey | null>(null);
  const wrappedRef = useRef<WrappedPrivateKey | null>(null);

  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;
    setStep('preparing');
    setName(defaultPasskeyName());
    (async () => {
      try {
        const identity = await createPendingIdentity();
        if (cancelled) {
          discardPendingIdentity(identity);
          return;
        }
        identityRef.current = identity;
        setStep('passkey');
      } catch (err: any) {
        if (!cancelled) {
          setError(err?.message || 'Could not generate your encryption keys.');
          setStep('failed');
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, prepareAttempt]);

  const discard = () => {
    discardPendingIdentity(identityRef.current);
    identityRef.current = null;
    passkeyRef.current = null;
    wrappedRef.current = null;
    setRecoveryKey(null);
  };

  const handleUnsupportedClose = () => {
    discard();
    onUnsupported();
  };

  const finish = async (keys: UserKeys) => {
    const identity = identityRef.current;
    if (!identity) return;
    holdSessionKeys(keys.publicKey, identity.privateKey, 'setup');
    await trustBrowser(uid, keys.publicKey, identity.privateKey, keys.trustEpoch);
    identityRef.current = null;
    onComplete(keys);
  };

  const upload = async () => {
    const identity = identityRef.current;
    const passkey = passkeyRef.current;
    const wrapped = wrappedRef.current;
    if (!identity || !passkey || !wrapped) return;
    setStep('uploading');
    setError(null);
    try {
      const keys = await publishIdentity(buildPublishBody(identity, wrapped, { name: name.trim() || defaultPasskeyName(), ...passkey }));
      await finish(keys);
    } catch (err: any) {
      if (err?.code === 'identity_exists') {
        discard();
        onIdentityExists();
        return;
      }
      if (err?.code === 'challenge_expired') {
        // Keep the keypair, prfSalt, and recovery key; ask for one more passkey touch.
        passkeyRef.current = null;
        setNotice(CHALLENGE_EXPIRED_MESSAGE);
        setStep('passkey');
        return;
      }
      setError(err?.message || 'Could not publish your encryption keys. Please try again.');
      setStep('failed');
    }
  };

  const handleCreatePasskey = async () => {
    const identity = identityRef.current;
    if (!identity || busy) return;
    setBusy(true);
    setError(null);
    try {
      passkeyRef.current = await createIdentityPasskey({
        prfSalt: identity.prfSalt,
        purpose: 'setup',
        privateKey: identity.privateKey,
      });
      setNotice(null);
      if (recoveryKey && wrappedRef.current) {
        // A challenge_expired retry: the recovery-key steps are not repeated.
        await upload();
      } else {
        setRecoveryKey(generateRecoveryKey());
        setStep('recovery');
      }
    } catch (err: any) {
      if (err?.code === 'identity_exists') {
        discard();
        onIdentityExists();
        return;
      }
      if (err instanceof PasskeyError && err.reason === 'prf-unavailable') {
        setStep('unsupported');
      } else {
        setError(err?.message || 'Could not create a passkey. Please try again.');
      }
    } finally {
      setBusy(false);
    }
  };

  const handleRecoveryConfirmed = async () => {
    const identity = identityRef.current;
    if (!identity || !recoveryKey) return;
    setBusy(true);
    try {
      wrappedRef.current = await wrapForRecoveryKey(identity.privateKey, recoveryKey);
      await upload();
    } finally {
      setBusy(false);
    }
  };

  /** Retry: re-run the keypair generation when it failed before anything was wrapped, else the upload. */
  const handleRetry = async () => {
    if (!wrappedRef.current) {
      discard();
      setError(null);
      setPrepareAttempt((attempt) => attempt + 1);
      return;
    }
    await handleRetryUpload();
  };

  const handleRetryUpload = async () => {
    const identity = identityRef.current;
    if (!identity) return;
    setBusy(true);
    try {
      // The earlier publish may have landed although the response was lost.
      const landed = await publishLanded(identity.publicKey).catch(() => null);
      if (landed) {
        await finish(landed);
        return;
      }
      await upload();
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} maxWidth="xs" fullWidth className="ph-no-capture" PaperProps={{ className: 'ph-no-capture' }} aria-labelledby="chat-key-setup-title">
      <DialogTitle id="chat-key-setup-title" sx={{ fontWeight: 600 }}>
        Set up chat encryption
      </DialogTitle>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '8px !important' }}>
        {step === 'preparing' && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
            <CircularProgress size={28} aria-label="Generating encryption keys" />
          </Box>
        )}

        {step === 'passkey' && (
          <>
            <DialogContentText sx={{ fontSize: '0.875rem' }}>
              Your messages are end-to-end encrypted with a key created in this browser. A passkey —
              synced by your Apple or Google account — unlocks it on any device with a touch.
            </DialogContentText>
            {notice && <Alert severity="info">{notice}</Alert>}
            {error && <Alert severity="error">{error}</Alert>}
            <TextField
              label="Passkey name"
              value={name}
              onChange={(event) => setName(event.target.value.slice(0, 64))}
              size="small"
              fullWidth
              disabled={busy}
              inputProps={{ maxLength: 64 }}
            />
          </>
        )}

        {step === 'recovery' && recoveryKey && (
          <RecoveryKeyStep
            recoveryKey={recoveryKey}
            email={email}
            onConfirmed={handleRecoveryConfirmed}
            confirmLabel="Finish setup"
            disabled={busy}
          />
        )}

        {step === 'uploading' && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 2 }} role="status">
            <CircularProgress size={20} />
            <DialogContentText sx={{ fontSize: '0.875rem' }}>Publishing your encryption keys…</DialogContentText>
          </Box>
        )}

        {step === 'unsupported' && <Alert severity="error">{PASSKEY_UNSUPPORTED_MESSAGE}</Alert>}

        {step === 'failed' && <Alert severity="error">{error || 'Setup failed.'}</Alert>}
      </DialogContent>
      {step === 'passkey' && (
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button variant="contained" fullWidth onClick={handleCreatePasskey} disabled={busy} sx={{ minHeight: 40 }}>
            {busy ? <CircularProgress size={20} sx={{ color: '#fff' }} /> : 'Create passkey'}
          </Button>
        </DialogActions>
      )}
      {step === 'failed' && (
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button variant="contained" fullWidth onClick={handleRetry} disabled={busy}>
            Retry
          </Button>
        </DialogActions>
      )}
      {step === 'unsupported' && (
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button variant="contained" fullWidth onClick={handleUnsupportedClose}>
            Close
          </Button>
        </DialogActions>
      )}
    </Dialog>
  );
}
